import { useState } from 'react';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { GET_FLOW_RUN_SUMMARY_REQUEST, RUN_SOURCE_NOW_REQUEST } from 'js/constants/actionTypes';
import { useSelector } from 'react-redux';
import useEntities from 'new/state/entities/repository/useEntities';
import { useResourceMeta } from 'js/hooks/useResourceMeta';
import { RESOURCE_TYPE_DATA_SET, RESOURCE_TYPE_SOURCE } from 'js/constants/resourceTypes';
import { isSqlAutomation } from 'js/lib/ResourceUtils';
import { useDateRange } from 'js/components/data_flows/cards/FlowInsights/hooks/index';
import { FlowInsightsType, flowInsightsAPI } from '../api';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { useMetricsRepository } from 'new/state/metrics/repository/useMetricsRepository';

type OnChangeLogParam = {
  data: any;
  runId: string;
  exploreFlowLogs?: boolean;
};

type ResponseType = {
  actions: FlowInsightsType;
  isGettingFlowRunSummary: boolean;
  isRunningFlowNow: boolean;
  connectionCategory: string;
  connectionOrVendorType?: string;
  isSqlAutomation: boolean;
  flowRunSummary: Record<string, any>;
  pagination: {
    page: number;
    onPageChange: (page: number) => void;
  };
  dateRange: {
    range: any;
    isDirty: boolean;
    initialRange: any;
    initialStartDate: any;
    onRangeChange: (dateRange: any) => void;
    normalizeDateRange: (
      startDate: any,
      endDate: any,
    ) => {
      start: any;
      end: any;
    };
  };
  logsViewState: {
    log: OnChangeLogParam | null;
    onChangeLog: (a: OnChangeLogParam) => void;
  };
  expandedState: {
    expandedIndex: number;
    onItemExpanded: (index: number) => void;
  };
  skeletonState: {
    showSkeleton: boolean;
    onShowSkeleton: (show: boolean) => void;
  };
};

export function useFlowInsights(props: any): ResponseType {
  const { data: resource, isPreSelected } = props;

  const dispatch = useAppDispatch();
  const { getFlowRunSummary } = useMetricsRepository();
  const [page, setPage] = useState(1);
  const [log, setLog] = useState<OnChangeLogParam | null>(null);
  const [showSkeleton, setShowSkeleton] = useState(true);
  const [expandedIndex, setExpandedIndex] = useState(isPreSelected ? 0 : -1);

  const { data: dataSets } = useEntities('dataSets');
  const dateRangeState = useDateRange(resource, dataSets);
  const isRunningFlowNow = useIsLoading(RUN_SOURCE_NOW_REQUEST);
  const isGettingFlowRunSummary = useIsLoading(GET_FLOW_RUN_SUMMARY_REQUEST);
  const { connectionCategory, connectionOrVendorType } = useResourceMeta(
    resource.type === 'SOURCE' ? RESOURCE_TYPE_SOURCE : RESOURCE_TYPE_DATA_SET,
    resource.id,
    resource.data_credentials,
  );

  const entities = useSelector((state: any) => state.entities);
  const source = resource.type === 'SOURCE' ? entities?.sources?.[resource.id] : null;
  const isSqlAutomationSource = !!source && isSqlAutomation(source, entities?.vendors ?? {});

  const flowRunSummary = getFlowRunSummary();

  return {
    actions: flowInsightsAPI(dispatch),
    flowRunSummary,
    connectionCategory,
    connectionOrVendorType,
    isSqlAutomation: isSqlAutomationSource,
    isRunningFlowNow: isRunningFlowNow,
    isGettingFlowRunSummary: isGettingFlowRunSummary,
    pagination: { page, onPageChange: setPage },
    dateRange: { ...dateRangeState },
    logsViewState: { log, onChangeLog: setLog },
    expandedState: { expandedIndex, onItemExpanded: setExpandedIndex },
    skeletonState: { showSkeleton, onShowSkeleton: setShowSkeleton },
  };
}
